
import React, { useState, ReactNode, cloneElement } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    TextField,
    Card,
    CardContent,
    Avatar
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses'
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import {useTheme, alpha} from '@mui/material/styles';
import {SparkLineChart} from "@mui/x-charts/SparkLineChart";
import {LayoutFieldModel,
        IconType,
        IconJSON} from '../../models/models';
import {validateEmailField} from '../../utils/fieldValidations';
import {checkLayoutFieldRequirement} from '../../utils/fieldValidations';
import {KPICardFigure,
        KPICardText,
        KPICardIcon,
        KPICardTrend,
        KPICardSparkline} from '../../models/modelsDashboard';
import {CustomIconComponent} from '../icons/CustomIcon';




export const KPICardFigureComponent: React.FC<{
  figure: KPICardFigure
}> = ({
  figure
}) => {


  /******************************/

  const renderTitle = (title: KPICardText) => {
    const color = title.color ? title.color : "text.secondary";
    const weight = title.weight === 'bold' ? 700: 500;
    return (
      <Typography
        variant="body2"
        sx={{
          color: color,
          fontWeight: weight
        }}
      >
        {title.text}
      </Typography>
    )
  }

  /******************************/

  const renderSubTitle = (subtitle: KPICardText) => {
    const color = subtitle.color ? subtitle.color : "text.secondary";
    const weight = subtitle.weight === 'bold' ? 700: 500;
    return (
      <Typography
        variant="body2"
        sx={{
          color: color,
          fontWeight: weight
        }}
      >
        {subtitle.text}
      </Typography>
    )
  }

  /******************************/

  const renderBody = (body: KPICardText) => {
    const color = body.color ? body.color : "text.secondary";
    const weight = body.weight === 'bold' ? 700: 500;
    const fontSize = body.size ? body.size : undefined;
    return (
      <Typography
        variant="h3"
        sx={{
          marginTop: 2,
          color: color,
          fontWeight: weight,
          fontSize: fontSize
        }}
      >
        {body.text}
      </Typography>
    )
  }

  /******************************/

  const renderTrend = (trend: KPICardTrend) => {
    const direction = trend.direction ? trend.direction : 'same';

    const getColor = (direction: string) => {
      if(direction === 'up'){
        return 'green'
      }else if(direction === 'down'){
        return 'red'
      }
      return 'text.secondary'
    }

    const getIcon = (direction: string, color: string) => {
      if(direction === 'up'){
        return <TrendingUpIcon sx={{color: color}}/>
      }else if(direction === 'down'){
        return <TrendingDownIcon sx={{color: color}}/>
      }
      return <TrendingFlatIcon sx={{color: color}}/>
    }

    const color = getColor(direction)

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: "row",
          marginTop: 2,
          gap: 4
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            color: 'text.secondary'
          }}
        >
          {trend.label}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1
          }}
        >
          {getIcon(direction, color)}

          <Typography
            sx={{
              fontWeight: 600,
              color: color
            }}
          >
            {trend.value}
          </Typography>
        </Box>
      </Box>
    )
  }

  /******************************/

  const renderIcon = (icon: KPICardIcon) => {
    const color = icon.color ? icon.color: 'text.secondary'
    const backgroundColor = icon.background_color ? icon.background_color : 'inherit'
    const type = icon.type ? icon.type as IconType : 'MuiIcon' as IconType
    const name = icon.name ? icon.name : '';

    const thisIcon: IconJSON = {Type_sys: type,
                                Name_sys: name,
                                Colour_sys: color,
                                BackgroundColour_sys: backgroundColor
                              }

    return (
      <Avatar
        sx={{
          color: color,
          backgroundColor: alpha(color,0.12),
          width: 76,
          height: 76
        }}
      >
        <CustomIconComponent
          icon={thisIcon}
          sx={{fontSize: 38}}
        />
      </Avatar>
    )
  }

  /******************************/

  const renderSparkline = (sparkline: KPICardSparkline) => {
    const data = sparkline.data? sparkline.data : [];
    const color = sparkline.color ? sparkline.color : 'text.secondary'
    return (
      <SparkLineChart
        data={data}
        height={60}
        showTooltip={false}
        showHighlight={false}
        colors={[color]}
        area
        curve='natural' //or linear or spline like catmullRom or natural
        sx={{
          '& .MuiAreaElement-root': {
            opacity: 0.15
          }
        }}
      />
    )
  }


  /******************************/

  return (
    <Card
      elevation={2}
      sx={{borderRadius: 4,
           backgroundColor: 'white',
           border: '1px solid',
           borderColor: 'divider',
           boxShadow: 'none',
           transition: 'all 0.2s ease',
           '&:hover': {
             boxShadow: 3,
             transform: 'translateY(-2px)'
           },
           height: '100%'}}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            width: '100%',
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "top",
            gap: 4
          }}
        >
          {figure.data.icon &&
            renderIcon(figure.data.icon)}

          <Box sx={{marginLeft: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1}}>
            {figure.data.title &&
              renderTitle(figure.data.title)}

            {figure.data.body &&
              renderBody(figure.data.body)}

            {figure.data.subtitle &&
              renderSubTitle(figure.data.subtitle)}

            {figure.data.trend &&
              renderTrend(figure.data.trend)}

            {figure.data.sparkline &&
              renderSparkline(figure.data.sparkline)}

          </Box>

        </Box>


      </CardContent>



    </Card>
  )
}
